"use client";
import { useEffect, useRef, useState } from "react";
import { PrevButton, NextButton } from "./buttonsFlow";
const StepContainer = ({
  children,
  onNext,
  onPrev,
  canGoNext = true,
  canGoPrev = true,
  isLastStep = false,
  errors,
  disabled,
  isLoading = false,
  currentStepResume = false,
  isBancolombiaIcon = false,
  justification,
  nextText = "Continuar",
  prevText = "Atrás",
  finishText = "Enviar solicitud",
  isDesktop = false,
  title = "",
  subtitle = "",
}) => {
  const [isStepLoading, setIsStepLoading] = useState(true);
  const starsRef = useRef(null);

  useEffect(() => {
    const canvas = starsRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let W = 0;
    let H = 0;
    let stars = [];
    let animationId;

    const resize = () => {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
    };

    const createStar = () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      r: Math.random() * 1 + 0.2,
      a: Math.random(),
      da: (Math.random() * 0.005 + 0.001) * (Math.random() < 0.5 ? 1 : -1),
    });

    const init = () => {
      resize();
      stars = Array.from({ length: 160 }, createStar);
    };

    const draw = () => {
      ctx.clearRect(0, 0, W, H);

      for (const star of stars) {
        star.a += star.da;

        if (star.a <= 0.04 || star.a >= 1) {
          star.da *= -1;
        }

        ctx.beginPath();
        ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${star.a.toFixed(2)})`;
        ctx.fill();
      }

      animationId = requestAnimationFrame(draw);
    };

    window.addEventListener("resize", resize);

    init();
    draw();

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  useEffect(() => {
    setTimeout(() => setIsStepLoading(false), 4000);
  }, [isStepLoading, disabled]);

  // ─── Botones reutilizables ───────────────────────────────────
  // Botones mobile — isMobile=true
  const navButtonsMobile = (
    <>
      {canGoPrev && (
        <PrevButton onBack={onPrev} label={prevText} isMobile={true} />
      )}
      {canGoNext && (
        <NextButton
          onNext={onNext}
          label={nextText}
          finishLabel={finishText}
          isLastStep={isLastStep}
          isLoading={isLoading}
          disabled={!canGoNext || disabled}
          errors={errors}
          isResume={currentStepResume}
          isMobile={true} // ← solo aquí
          isBancolombiaIcon={isBancolombiaIcon} // ← solo aquí
        />
      )}
    </>
  );

  // Botones desktop — sin isMobile
  const navButtonsDesktop = (
    <>
      {canGoPrev && <PrevButton onBack={onPrev} label={prevText} />}
      {canGoNext && (
        <NextButton
          onNext={onNext}
          label={nextText}
          finishLabel={finishText}
          isLastStep={isLastStep}
          isLoading={isLoading}
          disabled={!canGoNext || disabled}
          errors={errors}
          isResume={currentStepResume}
        />
      )}
    </>
  );

  // En mobile usar navButtonsMobile, en desktop navButtonsDesktop

  // ─── MOBILE ─────────────────────────────────────────────────
  if (!isDesktop) {
    return (
      <div className="h-full flex flex-col">
        <div
          className="bg-linear-to-br from-purple-light via-background to-muted flex flex-col justify-between"
          style={{
            margin: 0,
            height: "100vh",
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
        >
          <div
            className="w-full sm:max-w-md mx-auto px-4 sm:px-0 flex flex-col"
            style={{
              flex: 1,
              minHeight: 0,
              marginTop: "11px",
              justifyContent: justification,
              overflow: "hidden",
            }}
          >
            {children}
          </div>
        </div>

        {isStepLoading && <StepLoader isDesktop={isDesktop} starsRef={starsRef} />}

        <footer
          className="w-full sm:max-w-md mx-auto px-4 sm:px-0 block md:hidden fixed bottom-0"
          style={{
            paddingBottom: "calc(8px + env(safe-area-inset-bottom))",
            backgroundColor: "transparent",
            // zIndex: 9,
          }}
        >
          <div className="flex flex-col">
            <div className="w-full flex gap-4 items-center">
              {navButtonsMobile}
            </div>
            <div
              class="z-20 flex flex-col md:flex-row items-center justify-center gap-2.5 text-[#ffffff]/70 pointer-events-none select-none tracking-widest uppercase font-light text-[10px] text-center my-6 md:my-0 md:fixed md:w-auto md:left-[-290px] md:top-[50%] md:-translate-y-1/2 md:-rotate-90 md:origin-center">
              <div class="flex flex-row items-center justify-center gap-4 mr-2">
                <div class="flex items-center md:border-r border-[#ffffff]/30 pr-4 shrink-0 capitalize">
                  <img src="https://galilea.co/png/colombia-fintech.png" class="h-4 w-auto object-contain"
                    alt="colombia fintech" />
                </div>

                <div class="flex items-center md:border-r border-[#ffffff]/30 md:pr-4 shrink-0">
                  <img src="https://galilea.co/png/sic.png" class="h-4 w-auto object-contain brightness-0 invert opacity-90"
                    alt="Superintendencia de Industria y Comercio" />
                </div>
              </div>
              <span
                class="normal-case text-[11px] leading-tight tracking-normal text-[#ffffff]/70 max-w-[280px] md:max-w-[340px] font-semibold2">
                Galilea es una marca de GRUPO EMPRESARIAL ANAV S.A.S GEAA <br class="hidden md:inline" />registrada en
                Colombia con NIT: 901.582.748-5
              </span>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  // ─── DESKTOP ─────────────────────────────────────────────────
  return (
    <div
      className="relative flex flex-col justify-between"
      style={{
        width: "100%",
        height: "100%",
        padding: "clamp(20px, 4vh, 40px) clamp(20px, 4vw, 40px)",
        gap: "10px",
        alignItems: "flex-end",
      }}
    >
      <div style={{ width: "100%" }}>
        {title && (
          <h1
            style={{
              fontSize: "clamp(30px, 3vw, 60px)",
              fontWeight: 600,
              color: "#fff",
              marginBottom: subtitle ? "clamp(8px, 1.2vh, 16px)" : 0,
              lineHeight: "1.2",
              wordWrap: "break-word",
            }}
          >
            {title}
          </h1>
        )}
        {subtitle && (
          <p
            style={{
              fontSize: "clamp(10px, 1.1vw, 16px)",
              color: "#BEBCC2",
              lineHeight: "1.5",
              wordWrap: "break-word",
            }}
          >
            {subtitle}
          </p>
        )}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "-webkit-fill-available",
          width: "-webkit-fill-available",
          position: "relative",
        }}
      >
        {children}
      </div>

      {isStepLoading && <StepLoader isDesktop={isDesktop} starsRef={starsRef} />}

      <div
        className="flex gap-4 justify-end"
        style={{
          marginBottom: "10px",
          marginRight: "40px",
          width: "430px",
          alignItems: "center",
        }}
      >
        {navButtonsDesktop}
      </div>
    </div>
  );
};

const StepLoader = ({ isDesktop, starsRef }) => (
  <div
    className="absolute inset-0 z-50 flex flex-col items-center justify-center backdrop-blur-3xl"
    style={{
      backgroundColor: "#000000",
    }}
  >
    <canvas
      ref={starsRef}
      className="fixed inset-0 w-full h-full pointer-events-none"
    ></canvas>
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin" />
      <p className="text-white text-lg font-medium tracking-wide">
        Viajando...
      </p>
    </div>
  </div>
);

export default StepContainer;
